import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { FaFileInvoice } from "react-icons/fa";
import swal from "sweetalert";

const RequestQuoteListing = ({ metaId }) => {
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState({
    page: 1,
    count: 10,
    search: "",
    status: "request",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setFilters((prev) => ({ ...prev, page: 1 }));
    }, 400);

    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchQuotes = async () => {
    loader(true);

    const params = {
      page: filters.page,
      count: filters.count,
      status: filters.status,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    };

    try {
      const res = await ApiClient.get("request-quote/listing", params);

      if (res?.success) {
        const data = res?.data?.data || [];

        const filtered = data.filter((item) => item.status !== "reject");

        setQuotes(filtered);

        setTotal(res?.data?.total || filtered.length);
      }
    } catch (err) {
      console.error(err);
    }

    loader(false);
    setInitialFetchDone(true);
  };

  useEffect(() => {
    fetchQuotes();
  }, [filters.page, filters.count, filters.status, debouncedSearch]);

  const handlePageChange = (page) => {
    if (page < 1) return;
    setFilters((prev) => ({ ...prev, page }));
  };

  const openViewModal = (quote) => {
    setSelectedQuote(quote);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedQuote(null);
    navigate(window.location.pathname, { replace: true });
  };

  const handleGenerateQuote = (quote, type = "generate") => {
    navigate(`/${type}/quote/${quote?.id || quote?._id}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const fetchAndOpenQuote = async (id) => {
    loader(true);
    try {
      const res = await ApiClient.get("request-quote/listing", { id });
      if (res?.success) {
        const data = res?.data?.data || [];
        const found = data.find((item) => item._id === id || item.id === id);
        if (found) {
          setSelectedQuote(found);
          setIsViewModalOpen(true);
        } else {
          swal({
            title: "Quote Request Not Found",
            text: "The requested quote request could not be found.",
            icon: "error",
            button: "Close",
          });
          navigate(window.location.pathname, { replace: true });
        }
      }
    } catch (err) {
      console.error(err);
    }
    loader(false);
  };

  useEffect(() => {
    if (!metaId || !initialFetchDone) return;

    const found = quotes.find((est) => est._id === metaId || est.id === metaId);

    if (found) {
      setSelectedQuote(found);
      setIsViewModalOpen(true);
    } else {
      fetchAndOpenQuote(metaId);
    }
  }, [metaId, quotes, initialFetchDone]);

  return (
    <>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            <div className="relative w-full sm:w-[350px]">
              <input
                type="text"
                placeholder="Search by customer name, email..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    search: e.target.value,
                  }))
                }
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
              />
            </div>
          </div>

          {/* Mobile Card View */}
<div className="md:!hidden space-y-4">
  {quotes.length === 0 ? (
    <div className="bg-white border rounded-xl p-8 text-center">
      <p className="text-sm text-gray-500">No request quotes found</p>
    </div>
  ) : (
    quotes.map((item, index) => (
      <div
        key={item._id || index}
        className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm"
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center">
              <span className="text-xs font-semibold text-[#115B9D]">
                {getInitials(item.customer_name)}
              </span>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 capitalize">
                {item.customer_name || "—"}
              </h3>
              <p className="text-xs text-gray-500 break-all">
                {item.email || "—"}
              </p>
            </div>
          </div>

          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
              item.status === "request"
                ? "bg-blue-50 text-blue-600 border border-blue-100"
                : "bg-gray-50 text-gray-600 border border-gray-100"
            }`}
          >
            {item.status === "request"
              ? "Requested"
              : item?.status || "—"}
          </span>
        </div>

        {/* Details */}
        <div className="mt-4 flex gap-3">

          <div className="w-full">
            <p className="text-xs text-gray-500">Received</p>
            <div className="flex items-center gap-x-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900">
              {formatDate(item.createdAt)}
            </p>
            <p className="text-xs text-gray-500">
              {formatTime(item.createdAt)}
            </p>
            </div>
          </div>

                    <div className="w-[80px] min-w-[80px]">
            <p className="text-xs text-gray-500">Items</p>
            <p className="text-sm font-medium text-gray-900">
              {item.line_items?.length || 0}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2 border-t pt-3">

          {item?.status === "request" && (
            <button
              type="button"
              onClick={() => handleGenerateQuote(item)}
              className="flex-1 h-10 rounded-xl bg-[#115B9D] text-white text-sm font-medium hover:bg-[#0d4c84]"
            >
              Generate Quote
            </button>
          )}
                    <button
            type="button"
            onClick={() => openViewModal(item)}
            className="w-[80px] lex-1 h-10 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50"
          >
            View
          </button>
        </div>
      </div>
    ))
  )}
</div>

{/* Desktop Table View */}
<div className="max-md:!hidden overflow-auto border rounded-xl">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Customer",
                    "Email",
                    "Status",
                    "Total Items",
                    "Received",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-5">
                      <span className="text-sm text-gray-700 font-[400] whitespace-nowrap">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {quotes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">
                          No request quotes found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  quotes.map((item, index) => (
                    <tr
                      key={item._id || index}
                      className="hover:bg-gray-50/60 transition-colors"
                    >
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openViewModal(item)}
                            title="View"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                          {item?.status === "request" && (
                            <button
                              type="button"
                              onClick={() => handleGenerateQuote(item)}
                              title="Generate Estimate"
                              className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                            >
                              <FaFileInvoice className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-5 capitalize">
                        <div className="flex items-center gap-3">
                          <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-[#115B9D]">
                              {getInitials(item.customer_name)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 whitespace-nowrap capitalize">
                            {item.customer_name || "—"}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {item.email || "—"}
                        </p>
                      </td>
                      <td className="py-3 px-5">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                            item.status === "request"
                              ? "bg-blue-50 text-blue-600 border border-blue-100"
                              : "bg-gray-50 text-gray-600 border border-gray-100"
                          }`}
                        >
                          {item.status === "request"
                            ? "requested"
                            : item?.status || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-700">
                          {item.line_items?.length || 0} items
                        </p>
                      </td>
                      <td className="py-3 px-5">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {formatDate(item.createdAt)}
                        </p>
                        <p className="text-xs text-gray-600">
                          {formatTime(item.createdAt)}
                        </p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
</div>


          {total > filters.count && (
            <div className="flex flex-wrap gap-y-2 gap-x-5 items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(filters.page - 1) * filters.count + 1} to{" "}
                {Math.min(filters.page * filters.count, total)} of {total}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                >
                  Prev
                </button>

                {Array.from(
                  { length: Math.ceil(total / filters.count) },
                  (_, i) => i + 1,
                ).map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`px-3 py-1.5 text-sm rounded-lg ${
                      filters.page === p ? "bg-[#115B9D] text-white" : "border"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === Math.ceil(total / filters.count)}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeViewModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#115B9D]"
                        >
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Request Quote Details
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={closeViewModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {selectedQuote && (
                    <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">
                      <div className="flex items-center gap-2 sm:gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                        <div className="size-10 sm:size-14 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                          <span className="text-sm sm:text-lg font-bold text-[#115B9D]">
                            {getInitials(selectedQuote.customer_name)}
                          </span>
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900 capitalize">
                            {selectedQuote.customer_name || "—"}
                          </p>
                          <p className="text-sm text-gray-500">
                            {selectedQuote.email}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 border border-gray-100 rounded-xl bg-gray-50/30">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-3">
                            Billing Address
                          </p>
                          <p className="text-sm text-gray-700">
                            {selectedQuote.billingAddress?.bill_line1}
                          </p>
                          {selectedQuote.billingAddress?.bill_line2 && (
                            <p className="text-sm text-gray-700">
                              {selectedQuote.billingAddress?.bill_line2}
                            </p>
                          )}
                          <p className="text-sm text-gray-700">
                            {selectedQuote.billingAddress?.bill_city},{" "}
                            {selectedQuote.billingAddress?.bill_country}{" "}
                            {selectedQuote.billingAddress?.bill_postal_code}
                          </p>
                        </div>
                        <div className="p-4 border border-gray-100 rounded-xl bg-gray-50/30">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-3">
                            Shipping Address
                          </p>
                          <p className="text-sm text-gray-700">
                            {selectedQuote.shippingAddress?.ship_line1}
                          </p>
                          {selectedQuote.shippingAddress?.ship_line2 && (
                            <p className="text-sm text-gray-700">
                              {selectedQuote.shippingAddress?.ship_line2}
                            </p>
                          )}
                          <p className="text-sm text-gray-700">
                            {selectedQuote.shippingAddress?.ship_city},{" "}
                            {selectedQuote.shippingAddress?.ship_country}{" "}
                            {selectedQuote.shippingAddress?.ship_postal_code}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Line Items
                        </p>
                       {/* Mobile View */}
<div className="sm:hidden space-y-3">
  {selectedQuote.line_items?.map((item, idx) => (
    <div
      key={idx}
      className="border border-gray-100 rounded-xl p-4 bg-white"
    >
      <div className="mb-3">
        <p className="text-sm font-semibold text-[#115B9D]">
          {item.item_name ||
            item?.SalesItemLineDetail?.ItemRef?.name ||
            "--"}
        </p>
        <p className="text-[13px] text-gray-600 mt-1">
          {item.description || "--"}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <p className="text-[13px] text-gray-500">Qty</p>
          <p className="text-md font-medium text-gray-800">
            {item.qty ||
              item?.SalesItemLineDetail?.Qty ||
              "1"}
          </p>
        </div>

        <div>
          <p className="text-[13px] text-gray-500">Rate</p>
          <p className="text-md font-medium text-gray-800">
            {item.rate ||
            item?.SalesItemLineDetail?.UnitPrice
              ? `$${item.rate || item?.SalesItemLineDetail?.UnitPrice}`
              : "--"}
          </p>
        </div>

        <div className="text-right">
          <p className="text-[13px] text-gray-500">Amount</p>
          <p className="text-md font-semibold text-[#115B9D]">
            {item.amount ? `$${item.amount}` : "--"}
          </p>
        </div>
      </div>
    </div>
  ))}
</div>

{/* Desktop View */}
<div className="max-sm:hidden border border-gray-100 rounded-xl scrollbar overflow-auto">
  <table className="w-full text-sm">
    <thead className="bg-gray-50 text-gray-600 border-b border-gray-100">
      <tr>
        <th className="py-2.5 px-4 text-left font-medium">Item</th>
        <th className="py-2.5 px-4 text-center font-medium">Qty</th>
        <th className="py-2.5 px-4 text-right font-medium">Rate</th>
        <th className="py-2.5 px-4 text-right font-medium">Amount</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-50">
      {selectedQuote.line_items?.map((item, idx) => (
        <tr key={idx}>
          <td className="py-2.5 px-4">
            <div className="min-w-[150px]">
              <p className="font-medium text-gray-800">
                {item.item_name ||
                  item?.SalesItemLineDetail?.ItemRef?.name ||
                  "--"}
              </p>
              <p className="text-xs text-gray-600">
                {item.description || "--"}
              </p>
            </div>
          </td>

          <td className="py-2.5 px-4 text-center text-gray-600">
            {item.qty ||
              item?.SalesItemLineDetail?.Qty ||
              "1"}
          </td>

          <td className="py-2.5 px-4 text-right text-gray-600">
            {item.rate ||
            item?.SalesItemLineDetail?.UnitPrice
              ? `$${item.rate || item?.SalesItemLineDetail?.UnitPrice}`
              : "--"}
          </td>

          <td className="py-2.5 px-4 text-right font-medium text-gray-800">
            {item.amount ? `$${item.amount}` : "--"}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    {selectedQuote?.status === "request" && (
                      <button
                        type="button"
                        onClick={(e) => handleGenerateQuote(selectedQuote)}
                        className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                      >
                        <FaFileInvoice className="w-4 h-4" />
                        Generate Estimate
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={closeViewModal}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default RequestQuoteListing;
